import { Link, Navigate, useParams } from 'react-router-dom'
import { getBlogPostBySlug, blogListPath } from '../../content/blog'
import { useBlogSeo } from '../../seo/useBlogSeo'
import type { BlogPost } from '../../content/blog/types'
import type { SupportedLanguage } from '../../i18n'
import { MarketingHeader } from './MarketingHeader'
import { MarketingFooter } from './MarketingFooter'
import { useMarketingHeaderActions } from './useMarketingHeaderActions'

/** Chrome around the post content - not part of the post objects since it's generic UI copy, same
 *  pattern as SeoLandingPage's FAQ_TITLE/RELATED_LINKS_TITLE. */
const COPY: Record<SupportedLanguage, { backLink: string; relatedTitle: string; ctaLabel: string; dateLocale: string }> = {
  de: {
    backLink: '← Alle Artikel',
    relatedTitle: 'Weiterführende Themen',
    ctaLabel: 'Kostenlose PV-Bedarfsanalyse starten',
    dateLocale: 'de-DE',
  },
  pl: {
    backLink: '← Wszystkie artykuły',
    relatedTitle: 'Powiązane tematy',
    ctaLabel: 'Poproś teraz o bezpłatną ofertę',
    dateLocale: 'pl-PL',
  },
  es: {
    backLink: '← Todos los artículos',
    relatedTitle: 'Temas relacionados',
    ctaLabel: 'Solicitar presupuesto gratis ahora',
    dateLocale: 'es-ES',
  },
}

function formatDate(iso: string, dateLocale: string) {
  return new Date(iso).toLocaleDateString(dateLocale, { day: '2-digit', month: 'long', year: 'numeric' })
}

function BlogPostContent({ post }: { post: BlogPost }) {
  useBlogSeo(post)
  const headerActions = useMarketingHeaderActions()
  const copy = COPY[post.lang]

  return (
    <>
      <MarketingHeader {...headerActions} />
      <article className="blog-page blog-post">
        <Link to={blogListPath(post.lang)} className="legal-page-back">
          {copy.backLink}
        </Link>
        <h1>{post.title}</h1>
        <time dateTime={post.publishedAt}>{formatDate(post.publishedAt, copy.dateLocale)}</time>
        {post.sections.map((section, i) => {
          if (section.type === 'heading') return <h2 key={i}>{section.text}</h2>
          if (section.type === 'list')
            return (
              <ul key={i}>
                {section.items?.map((item) => <li key={item}>{item}</li>)}
              </ul>
            )
          return <p key={i}>{section.text}</p>
        })}
        <p className="blog-post-cta">
          {/* The free PV-Rechner tool is DE-only (see usePvRechnerSeo.ts) - PL/ES readers get a
              customer-signup CTA instead, same action as the SEO landing pages' primaryCta. */}
          {post.lang === 'de' ? (
            <Link to="/pv-rechner" className="btn-primary">
              {copy.ctaLabel}
            </Link>
          ) : (
            <button type="button" className="btn-primary" onClick={headerActions.onSelectCustomer}>
              {copy.ctaLabel}
            </button>
          )}
        </p>

        {post.relatedLinks && post.relatedLinks.length > 0 && (
          <nav className="seo-landing-related" aria-label={copy.relatedTitle}>
            <h2>{copy.relatedTitle}</h2>
            <ul>
              {post.relatedLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href}>{link.label} →</Link>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </article>
      <MarketingFooter />
    </>
  )
}

/** `lang` comes from the route (see App.tsx: /blog/:slug is DE, /pl/blog/:slug and /es/blog/:slug
 *  the translated posts). */
export function BlogPostPage({ lang = 'de' }: { lang?: SupportedLanguage }) {
  const { slug } = useParams<{ slug: string }>()
  const post = slug ? getBlogPostBySlug(slug, lang) : undefined

  if (!post) return <Navigate to={blogListPath(lang)} replace />

  return <BlogPostContent post={post} />
}
